import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import InventoryExpensesList from '../../components/InventoryExpensesList';
import { 
  FaEdit, 
  FaTrash, 
  FaArrowLeft, 
  FaBarcode, 
  FaPrint,
  FaExclamationTriangle,
  FaBoxOpen,
  FaDollarSign,
  FaMapMarkerAlt,
  FaTruck,
  FaCalendarAlt,
  FaStickyNote
} from 'react-icons/fa';

const InventoryDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    fetchInventoryItem();
  }, [id]);

  const fetchInventoryItem = async () => {
    try {
      const response = await fetch(`/api/inventory/${id}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setItem(data);
      } else {
        toast.error('Inventory item not found');
        navigate('/admin/inventory');
      }
    } catch (error) {
      console.error('Error fetching inventory item:', error);
      toast.error('Error fetching inventory item');
      navigate('/admin/inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`/api/inventory/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Item deleted successfully');
        navigate('/admin/inventory');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Error deleting item');
    }
  };

  const updateQuantity = async (newQuantity) => {
    try {
      const response = await fetch(`/api/inventory/${id}/quantity`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ quantity: newQuantity })
      });

      if (response.ok) {
        toast.success('Quantity updated successfully');
        fetchInventoryItem();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update quantity');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast.error('Error updating quantity');
    }
  };

  const printBarcode = () => {
    // Simple barcode printing - you might want to integrate with a proper barcode library
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Barcode - ${item.name}</title>
          <style>
            body { font-family: Arial, sans-serif; text-align: center; padding: 20px; }
            .barcode { font-size: 24px; font-weight: bold; margin: 20px 0; }
            .item-info { margin: 10px 0; }
          </style>
        </head>
        <body>
          <h2>${item.name}</h2>
          <div class="item-info">SKU: ${item.sku}</div>
          <div class="barcode">${item.barcode || 'No Barcode'}</div>
          <div class="item-info">Price: $${item.unitPrice}</div>
          <div class="item-info">Location: ${item.location || 'Not specified'}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getStockStatus = () => {
    if (!item) return { text: '', color: '' };
    
    if (item.quantity === 0) 
      return { text: 'Out of Stock', color: 'text-red-600 bg-red-100' };
    if (item.quantity <= item.minimumQuantity) 
      return { text: 'Low Stock', color: 'text-orange-600 bg-orange-100' };
    return { text: 'In Stock', color: 'text-green-600 bg-green-100' };
  };

  const isExpired = () => {
    if (!item?.expirationDate) return false;
    return new Date(item.expirationDate) < new Date();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Item Not Found</h2>
          <Link to="/admin/inventory" className="text-blue-600 hover:text-blue-800">
            Return to Inventory
          </Link>
        </div>
      </div>
    );
  }

  const stockStatus = getStockStatus();
  const expired = isExpired();

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/inventory')}
            className="text-gray-600 hover:text-gray-800"
          >
            <FaArrowLeft size={20} />
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{item.name}</h1>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={printBarcode}
            className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 flex items-center space-x-2"
          >
            <FaPrint />
            <span>Print Label</span>
          </button>
          
          {(user.role === 'admin' || user.role === 'manager') && (
            <Link
              to={`/admin/inventory/${id}/edit`}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center space-x-2"
            >
              <FaEdit />
              <span>Edit</span>
            </Link>
          )}
          
          {user.role === 'admin' && (
            <button
              onClick={() => setDeleteModalOpen(true)}
              className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 flex items-center space-x-2"
            >
              <FaTrash />
              <span>Delete</span>
            </button>
          )}
        </div>
      </div>

      {/* Status Alerts */}
      {(expired || item.quantity <= item.minimumQuantity) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {expired && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <div className="flex items-center">
                <FaExclamationTriangle className="text-red-400 mr-3" />
                <div>
                  <h3 className="text-lg font-medium text-red-800">Item Expired</h3>
                  <p className="text-red-700">This item expired on {formatDate(item.expirationDate)}</p>
                </div>
              </div>
            </div>
          )}
          
          {item.quantity <= item.minimumQuantity && (
            <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded">
              <div className="flex items-center">
                <FaExclamationTriangle className="text-orange-400 mr-3" />
                <div>
                  <h3 className="text-lg font-medium text-orange-800">Low Stock Alert</h3>
                  <p className="text-orange-700">Current quantity is at or below minimum level</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Information */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Basic Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">SKU</label>
                <p className="text-lg text-gray-900">{item.sku}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Barcode</label>
                <div className="flex items-center space-x-2">
                  <p className="text-lg text-gray-900">{item.barcode || 'Not assigned'}</p>
                  {item.barcode && <FaBarcode className="text-gray-400" />}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Category</label>
                <p className="text-lg text-gray-900">{item.category || 'Uncategorized'}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${stockStatus.color}`}>
                  {stockStatus.text}
                </span>
              </div>
            </div>

            {item.description && (
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="text-gray-900 mt-1">{item.description}</p>
              </div>
            )}
          </div>

          {/* Inventory Details */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaBoxOpen className="mr-2" />
              Inventory Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{item.quantity}</p>
                <p className="text-sm text-gray-500">Current Stock</p>
                <p className="text-xs text-gray-400">{item.unit}</p>
              </div>
              
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <p className="text-2xl font-bold text-orange-600">{item.minimumQuantity}</p>
                <p className="text-sm text-gray-500">Minimum Stock</p>
                <p className="text-xs text-gray-400">{item.unit}</p>
              </div>
              
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{item.maximumQuantity}</p>
                <p className="text-sm text-gray-500">Maximum Stock</p>
                <p className="text-xs text-gray-400">{item.unit}</p>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-gray-600">{item.unit}</p>
                <p className="text-sm text-gray-500">Unit Type</p>
              </div>
            </div>

            {(user.role === 'admin' || user.role === 'manager') && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    const newQuantity = prompt('Enter new quantity:', item.quantity);
                    if (newQuantity !== null && !isNaN(newQuantity)) {
                      updateQuantity(parseInt(newQuantity));
                    }
                  }}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                >
                  Update Quantity
                </button>
              </div>
            )}
          </div>

          {/* Notes */}
          {item.notes && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <FaStickyNote className="mr-2" />
                Notes
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap">{item.notes}</p>
            </div>
          )}

          {/* Related Expenses */}
          <div className="mt-6">
            <InventoryExpensesList inventoryId={item.id} />
          </div>
        </div>

        {/* Side Panel */}
        <div className="space-y-6">
          {/* Pricing Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaDollarSign className="mr-2" />
              Pricing
            </h2>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Unit Cost:</span>
                <span className="font-semibold">{formatCurrency(item.costPrice)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Unit Price:</span>
                <span className="font-semibold">{formatCurrency(item.unitPrice)}</span>
              </div>
              
              <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                <span className="text-gray-600">Margin:</span>
                <span className="font-semibold text-green-600">
                  {formatCurrency(item.unitPrice - item.costPrice)}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Value:</span>
                <span className="font-semibold text-blue-600">
                  {formatCurrency(item.quantity * item.costPrice)}
                </span>
              </div>
            </div>
          </div>

          {/* Location & Supplier */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Location & Supplier</h2>
            
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <FaMapMarkerAlt className="text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Location</p>
                  <p className="text-gray-900">{item.location || 'Not specified'}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <FaTruck className="text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Supplier</p>
                  <p className="text-gray-900">{item.supplier || 'Not specified'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaCalendarAlt className="mr-2" />
              Important Dates
            </h2>
            
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-500">Created</p>
                <p className="text-gray-900">{formatDate(item.createdAt)}</p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-500">Last Updated</p>
                <p className="text-gray-900">{formatDate(item.updatedAt)}</p>
              </div>
              
              {item.expirationDate && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Expiration Date</p>
                  <p className={`${expired ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                    {formatDate(item.expirationDate)}
                    {expired && <span className="ml-2 text-red-500">(Expired)</span>}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <FaTrash className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Inventory Item</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete "{item.name}"? This action cannot be undone.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300"
                >
                  Delete
                </button>
                <button
                  onClick={() => setDeleteModalOpen(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md w-24 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryDetailPage;
